import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationTranscript, PERSON_ROLES, PHRASE_TYPES } from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { DialogSessionActions } from '@/features/DialogSession';
import { cn, formatTimeInterval } from '@/shared/lib';

type Props = {
  transcriptData: ConversationTranscript[];
  audioDuration: number;
};

type SpeakerSegment = {
  startMs: number;
  endMs: number;
  personId: number;
};

/**
 * PersonId участника с ролью EMPLOYEE (отображается сверху). Если такого нет — null.
 */
function getEmployeePersonId(transcriptData: ConversationTranscript[]): number | null {
  for (const msg of transcriptData) {
    if (msg.type !== PHRASE_TYPES.PHRASE || !msg.person?.id) continue;
    if (msg.person.role === PERSON_ROLES.EMPLOYEE) return msg.person.id;
  }
  return null;
}

/**
 * Уникальные personId из фраз в порядке первого появления (fallback для определения верх/низ).
 */
function getOrderedSpeakerIds(transcriptData: ConversationTranscript[]): number[] {
  const seen = new Set<number>();
  const order: number[] = [];
  for (const msg of transcriptData) {
    if (msg.type !== PHRASE_TYPES.PHRASE || !msg.person?.id) continue;
    const id = msg.person.id;
    if (!seen.has(id)) {
      seen.add(id);
      order.push(id);
    }
  }
  return order;
}

/**
 * Интервалы реплик по говорящим: один человек — полоса сверху по центру оси, второй — снизу.
 * Данные берутся из transcriptData (тип PHRASE с person и временными метками).
 */
export const SpeakerIntervals: FC<Props> = ({ audioDuration, transcriptData }) => {
  const { t } = useTranslation('dialogs');
  // Дорожка идёт по оси плеера, и её метки сопоставляются с его временем — формат общий с ним
  const timeDisplayFormat = useTimeDisplayFormat('player');
  const totalDurationMs = audioDuration * 1000;

  const employeePersonId = useMemo(() => getEmployeePersonId(transcriptData), [transcriptData]);
  const orderedSpeakerIds = useMemo(() => getOrderedSpeakerIds(transcriptData), [transcriptData]);

  const segments = useMemo<SpeakerSegment[]>(() => {
    if (!transcriptData.length || !totalDurationMs) return [];

    return transcriptData
      .filter((msg) => msg.type === PHRASE_TYPES.PHRASE && msg.person?.id != null)
      .filter((msg) => msg.startedAt != null && msg.finishedAt != null)
      .map((msg) => ({
        startMs: msg.startedAt,
        endMs: msg.finishedAt!,
        personId: msg.person!.id,
      }))
      .filter((s) => s.startMs < s.endMs);
  }, [transcriptData, totalDurationMs]);

  if (!totalDurationMs || segments.length === 0 || orderedSpeakerIds.length === 0) {
    return null;
  }

  const topPersonId = employeePersonId ?? orderedSpeakerIds[0];

  return (
    <div className={cn('absolute inset-0 z-1', 'pointer-events-none')}>
      {segments.map((segment, index) => {
        const startPercent = (segment.startMs / totalDurationMs) * 100;
        const widthPercent = ((segment.endMs - segment.startMs) / totalDurationMs) * 100;
        const isTop = segment.personId === topPersonId;

        return (
          <DialogSessionActions
            key={`${segment.personId}-${segment.startMs}-${index}`}
            time={segment.startMs / 1000}
            actions={['play', 'stop']}
            clickAction="scrollTo"
            asChild
          >
            <button
              type="button"
              className={cn(
                'pointer-events-auto',
                'absolute left-0 rounded-sm text-xs border-0 p-0 cursor-pointer transition-opacity hover:opacity-60',
                isTop
                  ? 'top-0 h-1/2 bg-(--color-chat-cloud-outgoing)/25 border-t border-(--color-chat-cloud-outgoing)/40'
                  : 'bottom-0 h-1/2 bg-orange-400/25 border-b border-orange-400/40',
              )}
              style={{
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
              }}
              aria-label={t('timeFragment.goToPhraseAt', {
                time: formatTimeInterval(segment.startMs / 1000, timeDisplayFormat),
              })}
            />
          </DialogSessionActions>
        );
      })}
    </div>
  );
};
